import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import I18n from 'i18n-js';
import {
  set_current_year_path,
  top_menu_field_groups_path,
  top_menu_machine_regions_path,
  set_current_production_cycles_path,
  set_current_machine_regions_path,
  work_recording_start_path,
  work_recording_timer_data_path,
} from 'js-routes/generated/routes';
import {
  arrayOf, bool, func, number, oneOfType, shape, string,
} from 'prop-types';
import {
  Spin,
  Badge,
  Space,
  Button,
  Drawer,
  Select,
  Collapse,
  notification,
  Tooltip,
} from 'antd';
import { isOrchard, isMachinery } from 'helpers/userSessionSettings';
import { camelizeKeys } from 'humps';
import axios from 'axios';
import { capitalize, isEmpty, isEqual } from 'lodash-es';
import { DownOutlined, WarningFilled } from '@ant-design/icons';
import { InfoIcon } from 'components/cwo-icons';
import flattenTopMenuItemsToMap from 'helpers/flattenTopMenuItemsToMap';
import toggleBemModifier from 'helpers/toggle-bem-modifier';
import ProductionCyclesSettings from './nested/ProductionCyclesSettings';
import prepareItems, { ALL_GROUPS_KEY } from './helpers/prepareItems';
// eslint-disable-next-line import-x/no-rename-default
import preparedMachineryRegionsItems from './machinery-regions-helpers/prepareItems';
import initializeCheckedKeys from './helpers/initializeCheckedKeys';
import defineTreeKeys from './helpers/defineTreeKeys';
import defineApplyUrl from './helpers/defineApplyUrl';
import defineApplyRegionIds from './machinery-regions-helpers/defineApplyRegionIds';
import checkedKeysCompare from './helpers/checkedKeysCompare';
import FieldGroupsSettings from './nested/FieldGroupsSettings';
import MachineRegionsSettings from './nested/MachineRegionsSettings';
import usePostRequest from './hooks/usePostRequest';
import useDispatcherTimer from './hooks/useDispatcherTimer';
import pageReload from './helpers/pageReload';
import useDrawerHeight from './hooks/useDrawerHeight';
import ToggleBtnGroup from './nested/ToggleBtnGroup';

const TopMenuFieldGroups = ({
  options = {
    fieldGroupsValue: ' - ',
    machineRegionsValue: ' - ',
    productionCycleValue: '',
    seasonsByYears: {},
    settings: {},
    seasonValue: 2000,
    productionCyclesBySeasons: [],
    showAllMachineRegions: false,
    isShowCompanies: false,
    isShowFieldGroups: true,
    isShowProductionCycles: true,
    isShowMachineRegions: true,
  },
  filterDrawerOpen = false,
  filterCollapseActiveKey = 1,
  setFilterDrawerOpen,
  setFilterCollapseActiveKey,
}) => {
  const {
    fieldGroupsValue,
    seasonsByYears,
    productionCycleValue,
    seasonValue,
    machineRegionsValue,
    settings,
  } = options;

  const {
    currentYear,
    currentProductionCycleIds,
    showDispatcherWorkRecord,
    showAllGroups,
    showAllMachineRegions,
    productionCyclesBySeasons,
  } = settings;

  const { current: initSettings } = useRef({
    expandKeys: [],
    expandAll: false,
    expandAllRegions: true,
    expandedRegionsKeys: [],
    initialCheckedKeys: { checked: [], halfChecked: [] },
    initialRegionsCheckedKeys: { checked: [], halfChecked: [] },
  });

  const visible = false;
  const [fieldGroups, setFieldGroups] = useState([]);
  const [machineRegions, setMachineRegions] = useState([]);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [sendingMachineRegionsRequest, setSendingMachineRegionsRequest] = useState(false);
  const [plainItemsIndex, setPlainItemsIndex] = useState(new Map());
  const [plainRegionsItemsIndex, setRegionsPlainItemsIndex] = useState(new Map());
  const [treeKeys, setTreeKeys] = useState(initSettings.initialCheckedKeys);
  const [regionsTreeKeys, setRegionsTreeKeys] = useState(initSettings.initialRegionsCheckedKeys);
  const [applyUrl, setApplyUrl] = useState(null);
  const [applyRegionIds, setApplyRegionIds] = useState(null);
  const [noEvalableRegions, setNoEvalableRegions] = useState(false);
  const postRequest = usePostRequest();

  const loadMachineRegionsData = useCallback(async () => {
    if (!isEmpty(machineRegions) || sendingMachineRegionsRequest) {
      return;
    }

    setSendingMachineRegionsRequest(true);

    const url = top_menu_machine_regions_path();
    const data = await postRequest(url);
    const { items, expandAll, expandKeys } = camelizeKeys(data);
    if (isEmpty(items)) {
      setNoEvalableRegions(true);
      setSendingMachineRegionsRequest(false);
      return;
    }
    const machineryRegionsFmt = preparedMachineryRegionsItems(items || []);
    const plainMapIndex = flattenTopMenuItemsToMap(machineryRegionsFmt);
    setMachineRegions(items);
    initSettings.expandAllRegions = expandAll;
    initSettings.expandedRegionsKeys = expandKeys;
    initSettings.initialRegionsCheckedKeys =
      initializeCheckedKeys(plainMapIndex, showAllMachineRegions);

    setRegionsTreeKeys(initSettings.initialRegionsCheckedKeys);
    setRegionsPlainItemsIndex(plainMapIndex);
    setMachineRegions(machineryRegionsFmt);

    setSendingMachineRegionsRequest(false);
    setNoEvalableRegions(false);
  }, [
    postRequest,
    machineRegions,
    showAllMachineRegions,
    sendingMachineRegionsRequest,
    initSettings,
  ]);

  const loadFieldGroupsData = useCallback(async () => {
    if (!isEmpty(fieldGroups) || sendingRequest) {
      return;
    }

    setSendingRequest(true);

    const url = top_menu_field_groups_path();
    const data = await postRequest(url);
    const { items, expandAll, expandKeys } = camelizeKeys(data);
    const fieldGroupsFmt = prepareItems(items || [], showAllGroups);
    const plainMapIndex = flattenTopMenuItemsToMap(fieldGroupsFmt);

    initSettings.expandAll = expandAll;
    initSettings.expandKeys = expandKeys;
    initSettings.initialCheckedKeys = initializeCheckedKeys(plainMapIndex, showAllGroups);

    setTreeKeys(initSettings.initialCheckedKeys);
    setPlainItemsIndex(plainMapIndex);
    setFieldGroups(fieldGroupsFmt);

    setSendingRequest(false);
  }, [
    postRequest,
    fieldGroups,
    initSettings,
    sendingRequest,
    showAllGroups,
  ]);

  const loadMenuData = useCallback(async () => {
    loadFieldGroupsData();
    loadMachineRegionsData();
  }, [
    loadFieldGroupsData,
    loadMachineRegionsData,
  ]);

  const onCheck = (_, { checked: nodeIsChecked, node }) => {
    const newTreeKeys = defineTreeKeys({
      plainItemsIndex,
      node,
      nodeIsChecked,
      currentTreeKeys: treeKeys,
      allGroupsKey: ALL_GROUPS_KEY,
    });

    const newCheckedArr = [...newTreeKeys.checked];
    const newHalfCheckedArr = [...newTreeKeys.halfChecked];

    setTreeKeys({
      checked: newCheckedArr,
      halfChecked: newHalfCheckedArr,
    });

    const { initialCheckedKeys: { checked, halfChecked } } = initSettings;
    const isCheckedEqual = checkedKeysCompare(checked, newCheckedArr);
    const isHalfCheckedEqual = checkedKeysCompare(halfChecked, newHalfCheckedArr);

    const initialSelectionWasModified = !isCheckedEqual || !isHalfCheckedEqual;

    setApplyUrl(
      initialSelectionWasModified
        ? defineApplyUrl(newTreeKeys.checked, plainItemsIndex)
        : null,
    );
  };

  const onCheckRegion = (_, { checked: nodeIsChecked, node }) => {
    const newTreeKeys = defineTreeKeys({
      plainItemsIndex: plainRegionsItemsIndex,
      node,
      nodeIsChecked,
      currentTreeKeys: regionsTreeKeys,
    });

    const newCheckedArr = [...newTreeKeys.checked];
    const newHalfCheckedArr = [...newTreeKeys.halfChecked];

    setRegionsTreeKeys({
      checked: newCheckedArr,
      halfChecked: newHalfCheckedArr,
    });

    const { initialRegionsCheckedKeys: { checked, halfChecked } } = initSettings;
    const isCheckedEqual = checkedKeysCompare(checked, newCheckedArr);
    const isHalfCheckedEqual = checkedKeysCompare(halfChecked, newHalfCheckedArr);

    const initialSelectionWasModified = !isCheckedEqual || !isHalfCheckedEqual;

    setApplyRegionIds(
      initialSelectionWasModified
        ? defineApplyRegionIds(newTreeKeys.checked, plainRegionsItemsIndex)
        : null,
    );
  };

  const fieldGroupsFilterClasses = toggleBemModifier(
    'top-menu-field-group-filter__filter',
    'loading',
    sendingRequest,
  );

  const machineRegionssFilterClasses = toggleBemModifier(
    'top-menu-field-group-filter__regions_filter',
    'loading',
    sendingMachineRegionsRequest,
  );

  const [sendingChanges, setSendingChanges] = useState(false);
  const [currentSeason, setCurrentSeason] = useState(currentYear);
  const [checkedProdCycleKeys, setCheckedProdCycleKeys] = useState(currentProductionCycleIds);

  const [dispatcherWorkRecord, setDispatcherWorkRecord] = useState({
    showWorkRecord: showDispatcherWorkRecord,
    initialSeconds: null,
    durationApproachingMessage: null,
  });

  useEffect(() => {
    if (showDispatcherWorkRecord) {
      const url = work_recording_timer_data_path();
      postRequest(url).then((data) => {
        setDispatcherWorkRecord({
          ...dispatcherWorkRecord,
          initialSeconds: data.initialSeconds,
          durationApproachingMessage: data.durationApproachingMessage,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMachineRegionsData]);

  const { showWorkRecord, initialSeconds } = dispatcherWorkRecord;

  const { Panel } = Collapse;

  const onClose = () => {
    setFilterDrawerOpen(false);
    setCurrentSeason(currentYear);
    setCheckedProdCycleKeys(currentProductionCycleIds);

    setTreeKeys(initSettings.initialCheckedKeys);
    setRegionsTreeKeys(initSettings.initialRegionsCheckedKeys);
    setApplyUrl(null);
    setApplyRegionIds(null);
  };

  const openCollapseActiveKey = (keyNumber) => {
    setFilterCollapseActiveKey(keyNumber);
  };

  const showDrawer = (collapseKey) => {
    setFilterDrawerOpen(true);
    openCollapseActiveKey(collapseKey);
  };

  const seasonSelectList = Object.entries(seasonsByYears)
    .map(([year, name]) => ({ value: year, label: `${name} (${year})` }))
    .reverse();

  const notificationMessage = (message) => {
    notification.warning({
      message,
      style: { backgroundColor: '#FFF3DD', width: 'auto' },
      placement: 'bottom',
      duration: 5,
      icon: <WarningFilled style={{ color: '#C17E19' }} />,
    });
  };

  const validateParams = () => {
    let isValid = true;

    if (checkedProdCycleKeys.length === 0) {
      notificationMessage(I18n.t('top_menu_right_part.please_select_product_cycle'));
      isValid = false;
    }

    if (treeKeys.checked.length === 0) {
      notificationMessage(
        I18n.t(`top_menu_right_part.please_select_${isOrchard ? 'orchard_plot' : 'field_group'}`),
      );
      isValid = false;
    }

    if ((regionsTreeKeys.checked.length === 0) && isMachinery && !noEvalableRegions) {
      notificationMessage(I18n.t('top_menu_right_part.please_select_machine_region'));
      isValid = false;
    }

    return isValid;
  };

  const changedFormParams = () => (
    currentSeason.toString() !== currentYear.toString()
    || !isEqual(checkedProdCycleKeys.sort(), currentProductionCycleIds.sort())
    || applyUrl || applyRegionIds
  );

  const saveFilterChanges = async () => {
    if (!changedFormParams()) return;

    if (currentSeason.toString() !== currentYear.toString()) {
      const currentSeasonUrl = set_current_year_path(currentSeason);
      await axios.get(currentSeasonUrl);
    }

    if (applyUrl) {
      const currentFieldGroupsUrl = applyUrl;
      await axios.get(currentFieldGroupsUrl);
    }

    /* eslint-disable camelcase */
    if (applyRegionIds) {
      await axios.get(set_current_machine_regions_path({
        machine_region_ids: applyRegionIds,
      }));
    }

    if (!isEqual(checkedProdCycleKeys, currentProductionCycleIds)) {
      const currentProductionCycleUrl = set_current_production_cycles_path(
        { production_cycle_ids: checkedProdCycleKeys },
      );
      await axios.get(currentProductionCycleUrl);
    }
    /* eslint-enable camelcase */
  };

  const saveChanges = async () => {
    if (!validateParams()) return;

    setSendingChanges(true);

    try {
      if (showWorkRecord && initialSeconds && applyRegionIds) {
        await axios.post(
          work_recording_start_path(),
          // eslint-disable-next-line camelcase
          { machine_region_ids: applyRegionIds },
        );
      }

      await saveFilterChanges();

      pageReload();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setSendingChanges(false);
    }
  };

  const currentDrawerHeight = useDrawerHeight(filterDrawerOpen);

  const drawerStyles = {
    header: {
      borderLeft: '5px solid #8354d6',
      flexDirection: 'row-reverse',
      paddingTop: '16px',
      paddingBottom: '16px',
      paddingLeft: '16px',
      paddingRight: '12px',
    },
    footer: {
      textAlign: 'right',
    },
  };

  const fieldGroupsPanelName = () => (
    <b>
      {I18n.t(
        `activerecord.attributes.field.${isOrchard ? 'orchard_plot' : 'field_group'}`,
        { count: 5 },
      )}
      <Badge
        size="small"
        count={treeKeys.checked.filter((key) => key.startsWith('g_')).length}
        showZero
        color="#c6e6ff"
        offset={[6, 0]}
        style={{
          boxShadow: 'none',
          color: '#0172cc',
          borderRadius: '10px',
          minWidth: '30px',
          minHeight: '20px',
          fontWeight: '600',
          lineHeight: '19px',
        }}
      />
    </b>
  );

  const productionCyclesPanelName = () => (
    <>
      <b>{capitalize(I18n.t('production_cycles.index.production_cycles'))}</b>
      <Badge
        size="small"
        count={checkedProdCycleKeys.length}
        showZero
        color="#fed7de"
        offset={[6, 0]}
        style={{
          boxShadow: 'none',
          color: '#bb485d',
          borderRadius: '10px',
          minWidth: '30px',
          minHeight: '20px',
          fontWeight: '600',
          lineHeight: '19px',
        }}
      />
    </>
  );

  const machineRegionsPanelName = () => (
    <b>
      {I18n.t('top_menu_right_part.machine_regions')}
      <Badge
        size="small"
        count={regionsTreeKeys.checked.filter((key) => key.startsWith('r_')).length}
        showZero
        color="#fee4ae"
        offset={[6, 0]}
        style={{
          boxShadow: 'none',
          color: '#98660e',
          borderRadius: '10px',
          minWidth: '30px',
          minHeight: '20px',
          fontWeight: '600',
          lineHeight: '19px',
        }}
      />
    </b>
  );

  const { timerString, isTimerRunning } = useDispatcherTimer(initialSeconds);

  const machineRegionsPanelContent = () => {
    if (noEvalableRegions) {
      return (<p>{I18n.t('top_menu_right_part.no_available_machine_regions')}</p>);
    }

    return (
      <MachineRegionsSettings
        items={machineRegions}
        checkedKeys={regionsTreeKeys}
        onCheck={onCheckRegion}
        plainItemsIndex={plainRegionsItemsIndex}
        visible={visible}
        expandAll={initSettings.expandAllRegions}
        expandKeys={initSettings.expandedRegionsKeys}
        drawerHeight={currentDrawerHeight}
        dispatcherWorkRecord={dispatcherWorkRecord}
        isTimerRunning={isTimerRunning}
        timerString={timerString}
        saveFilterChanges={saveFilterChanges}
        validateParams={validateParams}
      />
    );
  };

  return (
    <div className="top-menu-field-groups">
      <ToggleBtnGroup
        isMachinery={isMachinery}
        isTimerRunning={isTimerRunning}
        loadMenuData={loadMenuData}
        machineRegionsValue={machineRegionsValue}
        productionCycleValue={productionCycleValue}
        showDrawer={showDrawer}
        showWorkRecord={showWorkRecord}
        timerString={timerString}
        fieldGroupsValue={fieldGroupsValue}
        seasonValue={seasonValue}
        options={options}
      />

      <Drawer
        className="top-menu-drawer"
        closable
        // @ts-ignore
        styles={drawerStyles}
        placement="right"
        onClose={onClose}
        open={filterDrawerOpen}
        zIndex={1032}
        footer={(
          <Space>
            <Button
              className="top-menu-drawer__apply-button"
              type="primary"
              loading={sendingChanges}
              onClick={saveChanges}
              disabled={!changedFormParams()}
            >
              {I18n.t('apply')}
            </Button>
          </Space>
        )}
        extra={(
          <div id="season-panel" className="top-menu-field-group-filter__season-panel">
            <span><b>{I18n.t('top_menu_right_part.current_season')}</b></span>
            <Select
              className="top-menu-field-group-filter__season-select"
              id="season-select"
              showSearch
              defaultValue={currentSeason.toString()}
              value={currentSeason.toString()}
              onChange={(value) => setCurrentSeason(value)}
              filterOption={
                (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={seasonSelectList}
            />
            <Tooltip
              title={I18n.t(`top_menu_right_part.filter_info${isOrchard ? '_orchard' : ''}`)}
            >
              <span className="top-menu-field-group-filter__info-icon">
                <InfoIcon />
              </span>
            </Tooltip>
          </div>
        )}
      >
        <Collapse
          accordion
          expandIconPlacement="end"
          activeKey={filterCollapseActiveKey}
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon={({ isActive }) => (
            !isActive && <DownOutlined style={{ marginRight: '0px' }} />
          )}
          ghost
        >
          {options?.isShowFieldGroups && (
            <Panel
              header={fieldGroupsPanelName()}
              className="top-menu-field-group-filter__header
              top-menu-field-group-filter__field-groups"
              // @ts-ignore
              onClick={() => openCollapseActiveKey(1)}
              key="1"
              id="field-groups-panel"
            >
              <div className={fieldGroupsFilterClasses}>
                {isEmpty(fieldGroups) ? <Spin size="large" /> : (
                  <FieldGroupsSettings
                    items={fieldGroups}
                    checkedKeys={treeKeys}
                    onCheck={onCheck}
                    plainItemsIndex={plainItemsIndex}
                    visible={visible}
                    expandAll={initSettings.expandAll}
                    expandKeys={initSettings.expandKeys}
                    drawerHeight={currentDrawerHeight}
                  />
                )}
              </div>
            </Panel>
          )}

          {options?.isShowProductionCycles && (
            <Panel
              header={productionCyclesPanelName()}
              className="top-menu-field-group-filter__header
              top-menu-field-group-filter__production-cycles"
              // @ts-ignore
              onClick={() => openCollapseActiveKey(2)}
              key="2"
              id="production-cycles-panel"
            >
              <div className="top-menu-field-groups__production-cycle-filter">
                <ProductionCyclesSettings
                  currentYear={currentYear}
                  currentSeason={currentSeason}
                  productionCyclesBySeasons={productionCyclesBySeasons}
                  checkedProdCycleKeys={checkedProdCycleKeys}
                  setCheckedProdCycleKeys={setCheckedProdCycleKeys}
                  drawerHeight={currentDrawerHeight}
                />
              </div>
            </Panel>
          )}

          {(isMachinery && options?.isShowMachineRegions) && (
            <Panel
              header={machineRegionsPanelName()}
              className="top-menu-field-group-filter__header
                top-menu-field-group-filter__machine-regions"
              // @ts-ignore
              onClick={() => openCollapseActiveKey(3)}
              key="3"
              id="machine-regions-panel"
            >
              <div className={machineRegionssFilterClasses}>
                {(isEmpty(machineRegions) && sendingMachineRegionsRequest) ?
                  <Spin size="large" /> : machineRegionsPanelContent()}
              </div>
            </Panel>
          )}
        </Collapse>
      </Drawer>
    </div>
  );
};

TopMenuFieldGroups.propTypes = {
  options: shape({
    fieldGroupsValue: string,
    machineRegionsValue: string,
    settings: shape({
      availableYears: arrayOf(number),
      currentYear: number,
      showAdditionalObjects: bool,
      showAllGroups: bool,
      winterMode: bool,
      currentProductionCycleIds: arrayOf(number),
    }),
    seasonsByYears: shape({}).isRequired,
    productionCyclesBySeasons: arrayOf(shape({})),
    productionCycleValue: oneOfType([string, number]).isRequired,
    seasonValue: oneOfType([string, number]).isRequired,
    showAllMachineRegions: bool,
    isShowCompanies: bool,
    isShowFieldGroups: bool,
    isShowProductionCycles: bool,
    isShowMachineRegions: bool,
  }),
  filterDrawerOpen: bool,
  setFilterDrawerOpen: func.isRequired,
  filterCollapseActiveKey: number,
  setFilterCollapseActiveKey: func.isRequired,
};

export default TopMenuFieldGroups;
